'use client';

import type { LocaleCode } from '@ifixit/i18n/types';
import { setLanguagePreference, setLocalePreference } from '@ifixit/i18n/locale-preference';
import { type PhpLangid } from '@ifixit/i18n/php-langid';
import {
   type Country,
   type UserLocaleCode,
   countryByCode,
   parseUserLocale,
   routeLocaleByUserLocale,
} from '@ifixit/i18n/user-locale';
import { useIFixitApiClient } from '@ifixit/ifixit-api-client';
import { SentryError } from '@ifixit/sentry';
import { sendReactNativeMessage } from '@ifixit/utils';
import {
   createContext,
   type RefObject,
   use,
   useCallback,
   useEffect,
   useMemo,
   useRef,
   useState,
   type ReactNode,
} from 'react';
import { useCookieState, readCookie, notifyCookieChange } from '../hooks/use-cookie-state';
import { useDetectedUserLocale } from '../hooks/use-detected-user-locale';
import { useBrowserLanguage } from '../hooks/use-browser-language';
import {
   parseRawCookieValue,
   resolveUserLocaleForPhpLangId,
   resolveUserLocaleForRouteLocale,
} from './resolve-user-locale';

export interface SuggestedLocale {
   userLocale: UserLocaleCode;
   routeLocale: LocaleCode;
   country: Country;
}

interface LocationContextValue {
   isPickerOpen: boolean;
   openPicker: () => void;
   closePicker: () => void;
   selectionMadeRef: RefObject<boolean>;
   localeSuggestionMessages: LocaleSuggestionMessages | null;
   userLocale: UserLocaleCode;
   setUserLocale: (locale: UserLocaleCode) => Promise<unknown>;
   suggestedLocale: SuggestedLocale | null;
   currentRouteLocale: LocaleCode | null;
   currentPhpLangId: string | null;
   hrefFromUserLocale: (userLocale: UserLocaleCode) => string | null;
   isCarpenter: boolean;
   withProgressBar: (callback: () => Promise<void>) => Promise<void>;
}

export interface LocaleSuggestionMessages {
   locale: string;
   messages: Record<string, unknown>;
}

const LocationContext = createContext<LocationContextValue | null>(null);

export interface LocationProviderProps {
   currentRouteLocale?: LocaleCode;
   currentPhpLangId?: PhpLangid;
   geoDetectedUserLocale?: UserLocaleCode | null;
   geoPending?: boolean;
   hrefFromUserLocale: (userLocale: UserLocaleCode) => string | null;
   isCarpenter: boolean;
   withProgressBar: (callback: () => Promise<void>) => Promise<void>;
   localeSuggestionMessages?: Record<string, Record<string, unknown>>;
   children: ReactNode;
}

export function LocationProvider({
   currentRouteLocale,
   currentPhpLangId,
   geoDetectedUserLocale,
   geoPending = false,
   hrefFromUserLocale,
   isCarpenter,
   withProgressBar,
   localeSuggestionMessages = {},
   children,
}: LocationProviderProps) {
   const picker = usePickerState();
   const resolvedMessages = useSuggestionMessages(localeSuggestionMessages);
   const locale = useUserLocaleState({
      currentRouteLocale,
      currentPhpLangId,
      geoDetectedUserLocale,
      geoPending,
   });
   const suggestedLocale = useSuggestedLocale(currentRouteLocale, locale.rawCookieValue);

   const value = useMemo(
      () => ({
         ...picker,
         localeSuggestionMessages: resolvedMessages,
         userLocale: locale.userLocale,
         setUserLocale: locale.setUserLocale,
         suggestedLocale,
         currentRouteLocale: currentRouteLocale ?? null,
         currentPhpLangId: currentPhpLangId ?? null,
         hrefFromUserLocale,
         isCarpenter,
         withProgressBar,
      }),
      [
         picker,
         resolvedMessages,
         locale.userLocale,
         locale.setUserLocale,
         suggestedLocale,
         currentRouteLocale,
         currentPhpLangId,
         hrefFromUserLocale,
         isCarpenter,
         withProgressBar,
      ]
   );

   return <LocationContext value={value}>{children}</LocationContext>;
}

// --- Public hooks (consumer API) ---

export function useLocationPicker() {
   const { isPickerOpen, openPicker, closePicker, selectionMadeRef } = useLocationContext();
   return { isPickerOpen, openPicker, closePicker, selectionMadeRef };
}

export function useLocaleSuggestionMessages(): LocaleSuggestionMessages | null {
   return useLocationContext().localeSuggestionMessages;
}

export function useSuggestedLocaleChange(): SuggestedLocale | null {
   return useLocationContext().suggestedLocale;
}

export function useUserLocale(): [
   UserLocaleCode,
   (userLocale: UserLocaleCode) => Promise<unknown>,
] {
   const { userLocale, setUserLocale } = useLocationContext();
   return [userLocale, setUserLocale];
}

export function useUserCountry(): Country {
   const { userLocale } = useLocationContext();
   return countryByCode[parseUserLocale(userLocale).country];
}

export function useLocationNavigation() {
   const { currentRouteLocale, currentPhpLangId, hrefFromUserLocale, withProgressBar } =
      useLocationContext();
   return { currentRouteLocale, currentPhpLangId, hrefFromUserLocale, withProgressBar };
}

export function useIsCarpenter() {
   return useLocationContext().isCarpenter;
}

// --- Private hooks (implementation details) ---

function useLocationContext() {
   const context = use(LocationContext);
   if (!context) {
      throw new SentryError('useLocationContext must be used within LocationProvider');
   }
   return context;
}

function usePickerState() {
   const [isPickerOpen, setIsPickerOpen] = useState(false);
   const selectionMadeRef = useRef(false);

   return useMemo(
      () => ({
         isPickerOpen,
         openPicker: () => {
            selectionMadeRef.current = false;
            setIsPickerOpen(true);
         },
         closePicker: () => setIsPickerOpen(false),
         selectionMadeRef,
      }),
      [isPickerOpen]
   );
}

function useSuggestionMessages(
   localeSuggestionMessages: Record<string, Record<string, unknown>>
): LocaleSuggestionMessages | null {
   const browserLocale = useBrowserLanguage() ?? 'en';

   return useMemo(() => {
      const messages = localeSuggestionMessages[browserLocale] ?? localeSuggestionMessages['en'];
      return messages ? { locale: browserLocale, messages } : null;
   }, [localeSuggestionMessages, browserLocale]);
}

function useSuggestedLocale(
   currentRouteLocale: LocaleCode | undefined,
   rawCookieValue: string | null | undefined
): SuggestedLocale | null {
   const detectedUserLocale = useDetectedUserLocale();

   return useMemo(() => {
      if (!detectedUserLocale || currentRouteLocale == null) return null;
      const detectedRouteLocale = routeLocaleByUserLocale[detectedUserLocale];
      if (detectedRouteLocale === currentRouteLocale) return null;
      const storedPreference = parseRawCookieValue(rawCookieValue);
      if (storedPreference && routeLocaleByUserLocale[storedPreference] === currentRouteLocale) {
         return null;
      }
      const { country: countryCode } = parseUserLocale(detectedUserLocale);
      return {
         userLocale: detectedUserLocale,
         routeLocale: detectedRouteLocale,
         country: countryByCode[countryCode],
      };
   }, [detectedUserLocale, currentRouteLocale, rawCookieValue]);
}

const LOCALE_COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

function useUserLocaleState({
   currentRouteLocale,
   currentPhpLangId,
   geoDetectedUserLocale,
   geoPending = false,
}: {
   currentRouteLocale?: LocaleCode;
   currentPhpLangId?: PhpLangid;
   geoDetectedUserLocale?: UserLocaleCode | null;
   geoPending?: boolean;
}) {
   const apiClient = useIFixitApiClient();
   const [rawValue, setCookieValue] = useCookieState(USER_LOCALE_COOKIE, {
      path: '/',
      domain: getCookieDomain(),
      maxAge: LOCALE_COOKIE_MAX_AGE,
      sameSite: 'Lax',
   });
   const [phpViewLocale, setPhpViewLocale] = useCookieState('view-locale', {
      path: '/',
      domain: getCookieDomain(),
      secure: true,
      sameSite: 'None',
   });
   const [phpViewLangid] = useCookieState('view-langid');

   // Carpenter supplies a client-side geo guess (from /api/2.0/geo_locate);
   // Next.js relies on the detected-country cookie set by middleware. Fall back
   // to the middleware cookie when Carpenter's async guess hasn't resolved yet.
   const middlewareDetectedUserLocale = useDetectedUserLocale();
   const geoUserLocale = geoDetectedUserLocale ?? middlewareDetectedUserLocale ?? null;

   const signals = {
      cookieValue: rawValue,
      phpViewLocale,
      phpViewLangid,
      geoUserLocale,
   };

   const userLocale =
      currentRouteLocale == null
         ? resolveUserLocaleForPhpLangId(currentPhpLangId!, signals)
         : resolveUserLocaleForRouteLocale(currentRouteLocale, signals);

   const resolvedRouteLocale = routeLocaleByUserLocale[userLocale];
   useEffect(() => {
      // Don't persist the resolved locale until Carpenter geo guess has settled.
      if (geoPending) return;
      if (resolvedRouteLocale !== phpViewLocale) {
         setPhpViewLocale(resolvedRouteLocale);
      }
   }, [geoPending, resolvedRouteLocale, phpViewLocale, setPhpViewLocale]);

   useMigrateUserLocaleCookieToRootDomain();

   const setUserLocale = useCallback(
      (locale: UserLocaleCode) => {
         const { language } = parseUserLocale(locale);
         const routeLocale = routeLocaleByUserLocale[locale];
         setCookieValue(locale);
         setPhpViewLocale(routeLocale);
         sendReactNativeMessage({ type: 'locale_change', localeCode: routeLocale });
         return Promise.all([
            setLocalePreference(apiClient, routeLocale),
            setLanguagePreference(apiClient, language),
         ]);
      },
      [apiClient, setCookieValue, setPhpViewLocale]
   );

   return useMemo(
      () => ({ userLocale, setUserLocale, rawCookieValue: rawValue }),
      [userLocale, setUserLocale, rawValue]
   );
}

const USER_LOCALE_COOKIE = 'user-locale';

/**
 * Migrate the user-locale cookie from the current subdomain (e.g. www.ifixit.com)
 * to the root domain (.ifixit.com) so the preference is shared across subdomains.
 *
 * Runs once on mount. If a subdomain-scoped cookie exists, it is copied to the
 * root domain (unless a root-domain cookie already exists) and then deleted.
 */
function useMigrateUserLocaleCookieToRootDomain() {
   useEffect(() => {
      if (window.location.hostname === 'localhost') {
         return;
      }
      const valueBefore = readCookie(USER_LOCALE_COOKIE);
      if (valueBefore == null) return;

      // Delete the subdomain-scoped cookie (no domain = current hostname).
      document.cookie = `${USER_LOCALE_COOKIE}=;max-age=0;path=/`;

      const valueAfter = readCookie(USER_LOCALE_COOKIE);
      if (valueAfter == null) {
         // The cookie only existed on the subdomain — rewrite it to the root domain.
         const domain = getCookieDomain();
         document.cookie = `${USER_LOCALE_COOKIE}=${encodeURIComponent(valueBefore)};path=/;domain=${domain};max-age=${LOCALE_COOKIE_MAX_AGE};SameSite=Lax`;
      }

      notifyCookieChange();
   }, []);
}

function getCookieDomain(): string {
   if (typeof window === 'undefined') return '';
   const hostname = window.location.hostname;
   // Browsers reject cookies with `domain=.localhost`; use the bare hostname
   // so the cookie attaches host-only.
   if (hostname === 'localhost') return 'localhost';
   const parts = hostname.split('.');
   // Use the root domain (e.g. .ifixit.com from www.ifixit.com)
   if (parts.length > 2) {
      return '.' + parts.slice(-2).join('.');
   }
   return '.' + hostname;
}
